import { Component, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { GlobalErrorHandlerService } from '@app/core/services';
import { TimeService } from '@app/shared/helpers';
import { UserService } from '@app/core/http-services/user.service';
import { ColDef, GridReadyEvent, RowModelType } from 'ag-grid-community';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TableUtilityService } from '@app/shared/utilities/table-utility.service';
import { GlobalUtilityService } from '@app/shared/helpers/global-utility.service';
@Component({
  selector: 'app-logs',
  templateUrl: './logs.component.html',
  styleUrls: ['./logs.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LogsComponent {
  destroy$: Subject<boolean> = new Subject<boolean>();
  logsGridApi: any;
  logsList: any = [];
  searchInput: string = '';
  logsBreadcrumbs: any = [
    {
      label: 'Logs',
      active: true,
    },
  ];
  dateFilters: any = {
    start_date: this.timeService.getLastDayUtcTime('days', 30),
    end_date: this.timeService.getCurrentUTCTime(),
  };
  filterParams: any = {
    from_time: this.timeService.getLastDayUtcTime('days', 30),
    end_time: this.timeService.getCurrentUTCTime(),
  };
  pageSize: number = 50;
  page: number = 0;
  public rowModelType: RowModelType = 'serverSide';
  public cacheBlockSize = 50;
  appColDef: ColDef[] = [
    {
      field: 'date',
      headerName: 'Timestamp',
      sortable: true,
      minWidth: 200,
      suppressMenu: true,
      valueGetter: (params) =>
        params.data.date ? this.timeService.convertTimeTolll(params.data.date) : '-',
    },
    {
      field: 'user_name',
      headerName: 'Username',
      sortable: true,
      minWidth: 200,
      suppressMenu: true,
      valueGetter: (params) => (params.data.user_name ? params.data.user_name : '-'),
    },
    {
      field: 'connection',
      headerName: 'Connection',
      sortable: true,
      minWidth: 200,
      suppressMenu: true,
      valueGetter: (params) => (params?.data?.connection ? params.data.connection : '-'),
    },
    {
      field: 'description',
      headerName: 'Description',
      sortable: true,
      minWidth: 200,
      suppressMenu: true,
      valueGetter: (params) => (params.data.description ? params.data.description : '-'),
    },

    {
      field: 'client_name',
      headerName: 'Application Name',
      sortable: true,
      minWidth: 200,
      suppressMenu: true,
      valueGetter: (params) => (params.data.client_name ? params.data.client_name : '-'),
    },
    {
      field: 'user_agent',
      headerName: 'System',
      sortable: true,
      minWidth: 200,
      suppressMenu: true,
      valueGetter: (params) => (params.data.user_agent ? params.data.user_agent : '-'),
    },
  ];
  public defaultColDef: ColDef = {
    resizable: true,
    suppressMenu: true,

    icons: {
      sortAscending: '<i class="icon icon-arrow-up"/>',
      sortDescending: '<i class="icon icon-arrow-down"/>',
    },
  };
  rowModified: number = 0;
  @ViewChild('container', { read: ViewContainerRef })
  public container: ViewContainerRef;
  noRowsTemplate: string = '<span style="color: var(--color-gray-500); ">No Rows To Show</span>';
  constructor(
    private userService: UserService,
    private timeService: TimeService,
    private errorHandlerService: GlobalErrorHandlerService,
    public tableUtility: TableUtilityService,
    private utilityService: GlobalUtilityService
  ) {
    this.onSearchChange = this.utilityService.debounce(this.onSearchChange, 1000);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onGridReady(params: GridReadyEvent) {
    this.logsGridApi = params.api;
    const datasource = {
      getRows: (params) => {
        let bodyParams = {};
        let searchQuery: string = '';
        let dateQuery: string = '';
        let sortingKey = this.tableUtility.getSortingKey(params.request);

        if (this.searchInput) {
          searchQuery = `user_name: ${this.searchInput}*, connection: ${this.searchInput}*, client_name: ${this.searchInput}*, description: ${this.searchInput}*, user_agent: ${this.searchInput}*`;
        }
        dateQuery = `date:[${this.filterParams.from_time} TO ${this.filterParams.end_time}}`;

        bodyParams['q'] = `${searchQuery.length && searchQuery} ${dateQuery}`;
        if (params.request.startRow > 0) {
          this.page = params.request.startRow / this.pageSize;
        }
        bodyParams['page'] = this.page;
        bodyParams['per_page'] = this.pageSize;
        bodyParams['include_totals'] = true;
        if (sortingKey) {
          bodyParams['sort'] = sortingKey;
        }
        this.userService
          .getLogsListApi(bodyParams)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            async (response: any) => {
              if (response?.logs) {
                this.logsList = response.logs;
                params.success({
                  rowData: response.logs,
                  rowCount: response.total,
                });
              } else {
                params.fail();
              }
              this.logsGridApi.hideOverlay();
              if (response?.total > 0 && response?.logs?.length) {
                this.logsGridApi.hideOverlay();
              } else {
                this.logsGridApi.showNoRowsOverlay();
              }
            },
            (err) => {
              this.logsGridApi.hideOverlay();
              if (!this.logsList.length) {
                params.success({
                  rowData: [],
                  rowCount: 0,
                });
                this.logsGridApi.showNoRowsOverlay();
              }

              this.errorHandlerService.apiErrorHandle(err);
            }
          );
      },
    };
    params.api.setServerSideDatasource(datasource);
  }

  /**
   * Grid auto column fit to width
   */
  gridAutoColumnFit(params) {
    params.api.sizeColumnsToFit();
  }

  /**
   * Update search keywork on search enter
   */
  onSearchChange = (event) => {
    this.searchInput = event.target.value.trim();
    this.logsGridApi.onFilterChanged();
  };

  /**
   * Update date filter on change
   */
  onDateSelect(changedDates) {
    this.dateFilters = changedDates;
    this.formFilterString('date');
  }

  /**
   * Helps to create filter according to case
   * @param changedFilter
   */
  formFilterString(changedFilter) {
    if (changedFilter == 'date') {
      if (!!this.dateFilters?.end_date && this.dateFilters?.end_date != 'InvalideDate') {
        this.filterParams.end_time = this.timeService.getDateTimeUTcTZFormat(
          this.dateFilters.end_date
        );
      }
      if (!!this.dateFilters?.start_date && this.dateFilters?.start_date != 'InvalideDate') {
        this.filterParams.from_time = this.timeService.getDateTimeUTcTZFormat(
          this.dateFilters.start_date
        );
      }
    }
    this.logsGridApi.onFilterChanged();
  }
}
